import Information16 from '@carbon/icons-react/es/information/16';
import { Tooltip } from 'antd';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { PartnerCompany, PartnerPermissionName } from 'api/partner/types';
import { Button } from 'components/atoms/button';
import { Checkbox } from 'components/atoms/checkbox';
import {
  BodyShort01,
  ProductiveHeading06,
  ProductiveHeading07,
} from 'components/styles';
import { useGetAllPartnerPermissions } from 'queries/partner';
import {
  translationKeyForAccessLevelDescription,
  translationKeyForAccessLevelLabel,
} from '../../utils';

const Header = styled.div`
  ${ProductiveHeading07};
  padding: var(--spacing-04) var(--spacing-05);
  height: 48px;
  display: flex;
  align-items: center;
`;
const Label = styled.div`
  ${BodyShort01};
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  ${Button} {
    width: 16px;
    height: 16px;
    min-width: 0;
  }
`;
const Value = styled.div`
  ${ProductiveHeading06};
  font-size: var(--font-size-base);
`;
const Row = styled.div`
  display: flex;
  padding: var(--spacing-03) var(--spacing-05);
  margin-bottom: var(--spacing-02);
  gap: var(--spacing-03);
  ${Label} {
    width: 218px;
  }
`;
const Content = styled.div`
  padding: var(--spacing-04) 0;
  ${Row}:nth-child(2n) {
    background-color: var(--coolest-gray-10);
  }
`;
const TooltipForAccessLevel = () => (
  <>
    <strong>
      <FormattedMessage
        id={translationKeyForAccessLevelLabel(PartnerPermissionName.Permissive)}
      />
    </strong>
    <div>
      <FormattedMessage
        id={translationKeyForAccessLevelDescription(
          PartnerPermissionName.Permissive,
        )}
      />
    </div>
    <br />
    <strong>
      <FormattedMessage
        id={translationKeyForAccessLevelLabel(PartnerPermissionName.Moderate)}
      />
    </strong>
    <div>
      <FormattedMessage
        id={translationKeyForAccessLevelDescription(
          PartnerPermissionName.Moderate,
        )}
      />
    </div>
    <br />
    <strong>
      <FormattedMessage
        id={translationKeyForAccessLevelLabel(PartnerPermissionName.Restricted)}
      />
    </strong>
    <div>
      <FormattedMessage
        id={translationKeyForAccessLevelDescription(
          PartnerPermissionName.Restricted,
        )}
      />
    </div>
  </>
);

interface Props {
  partner: PartnerCompany;
}
export const PartnerInformation: FC<Props> = ({ partner }) => {
  const { data: partnerPermissions } = useGetAllPartnerPermissions();
  const partnerPermissionSetName = useMemo(
    () =>
      partnerPermissions?.find(
        (x) =>
          x.partnerPermissionSetId ===
          partner.accessLevel.partnerPermissionSetId,
      )?.partnerPermissionSetName,
    [partner.accessLevel.partnerPermissionSetId, partnerPermissions],
  );

  return (
    <>
      <Header>
        <FormattedMessage id="partners.detail.information.title" />
      </Header>
      <Content>
        <Row>
          <Label>
            <FormattedMessage id="partners.detail.information.table.website.label" />
          </Label>
          <Value>
            {partner.companyUrl ? (
              <a href={partner.companyUrl} target="_blank" rel="noreferrer">
                {partner.companyUrl}
              </a>
            ) : (
              '-'
            )}
          </Value>
        </Row>
        <Row>
          <Label>
            <FormattedMessage id="partners.detail.information.table.partner_admin.label" />
          </Label>
          <Value>
            {partner.admins.map(({ emailId }) => emailId).join(', ')}
          </Value>
        </Row>
        <Row>
          <Label>
            <FormattedMessage id="partners.detail.information.table.max_seats_available.label" />
            <Tooltip
              title={
                <FormattedMessage id="partners.new.drawer.form.max_seats_available.hint" />
              }
            >
              <Information16 />
            </Tooltip>
          </Label>
          <Value>
            {partner.usedSeat} / {partner.maxSeat}
          </Value>
        </Row>
        <Row>
          <Label>
            <FormattedMessage id="partners.detail.information.table.access_level.label" />
            <Tooltip title={<TooltipForAccessLevel />}>
              <Information16 />
            </Tooltip>
          </Label>
          <Value>
            {partnerPermissionSetName != null ? (
              <FormattedMessage
                id={translationKeyForAccessLevelLabel(partnerPermissionSetName)}
              />
            ) : (
              <>-</>
            )}
          </Value>
        </Row>
        <Row>
          <Label>
            <FormattedMessage id="partners.detail.information.table.active_projects.label" />
          </Label>
          <Value>{partner.activeProject}</Value>
        </Row>
        <Row>
          <Label>
            <FormattedMessage id="partners.detail.information.table.completed_projects.label" />
          </Label>
          <Value>{partner.completedProject}</Value>
        </Row>
        <Row>
          <Label>
            <FormattedMessage id="partners.detail.information.table.strategic_partner.label" />
            <Tooltip
              title={
                <FormattedMessage
                  id="partners.new.drawer.form.strategic_partner.hint"
                  values={{
                    br: () => (
                      <>
                        <br />
                        <br />
                      </>
                    ),
                  }}
                />
              }
            >
              <Information16 />
            </Tooltip>
          </Label>
          <Value>
            <Checkbox disabled checked={!partner.strategic} />
          </Value>
        </Row>
      </Content>
    </>
  );
};
